import { cn } from "@/lib/utils";
import { Link } from "wouter";

export function formatCustomerAddressLine(parts: {
  unitNumber?: string | null;
  address?: string | null;
  postalCode?: string | null;
}): string | null {
  const line = [parts.unitNumber, parts.address, parts.postalCode]
    .map((s) => String(s ?? "").trim())
    .filter(Boolean)
    .join(", ");
  return line.length > 0 ? line : null;
}

function hasContactValue(value: string | null | undefined): boolean {
  return Boolean(String(value ?? "").trim());
}

function ContactIndicator({ available, label }: { available: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-slate-600">{label}</span>
      {available ? (
        <span className="font-semibold text-green-700" aria-label={`${label} on file`}>
          ✓
        </span>
      ) : (
        <span className="font-semibold text-slate-400" aria-label={`${label} not on file`}>
          ✕
        </span>
      )}
    </span>
  );
}

export type ServiceTicketCustomerHeaderProps = {
  name: string;
  addressLine?: string | null;
  contact?: string | null;
  email?: string | null;
  nameHref?: string | null;
  className?: string;
};

/** Compact customer block for SVR / RO detail headers (name, address, contact indicators). */
export function ServiceTicketCustomerHeader({
  name,
  addressLine,
  contact,
  email,
  nameHref,
  className,
}: ServiceTicketCustomerHeaderProps) {
  const displayName = String(name ?? "").trim() || "—";
  const displayAddress = String(addressLine ?? "").trim() || "Address not on file";

  return (
    <div className={cn("mt-1 space-y-0.5", className)}>
      {nameHref ? (
        <Link
          href={nameHref}
          className="block w-fit text-base font-medium text-slate-800 underline-offset-4 transition hover:text-violet-800 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          title={`Open ${displayName}'s customer card`}
        >
          {displayName}
        </Link>
      ) : (
        <p className="text-base font-medium text-slate-800">{displayName}</p>
      )}
      <p className="text-sm text-slate-600 leading-snug">{displayAddress}</p>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm">
        <ContactIndicator available={hasContactValue(contact)} label="WhatsApp" />
        <ContactIndicator available={hasContactValue(email)} label="Email" />
      </p>
    </div>
  );
}
